// src/utils/generateAnimalPDF.js
import dayjs from "dayjs";
import jsPDF from "jspdf";

export const generateAnimalPDF = async (animals, logoUrl, summaryData = {}, pageTitle = "Price Comparison Data") => {
  const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
  const logoImg = await loadImage(logoUrl);

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Equal margins
  const margin = 40;
  const padding = 12;
  const bottomPadding = 30;

  const cardSpacing = 20; // space between cards
  const cardWidth = (pageWidth - 2 * margin - cardSpacing) / 2; // 2 cards per row
  const cardHeight = (pageHeight - margin - bottomPadding - cardSpacing) / 2; // 2 rows per page

  let x = margin;
  let y = margin + 20; // offset for header/logo

  // Header/logo/title
  drawHeader(doc, logoImg, pageWidth, margin, pageTitle);

  animals.forEach((animal, index) => {
    // Card background
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(x, y, cardWidth, cardHeight, 6, 6, "F");

    // Card border (subtle shadow effect)
    doc.setDrawColor(200);
    doc.setLineWidth(0.8);
    doc.roundedRect(x, y, cardWidth, cardHeight, 6, 6, "S");

    let cursorY = y + padding;

    // Photo
    if (animal.photo) {
      try {
        doc.addImage(animal.photo, "JPEG", x + padding, cursorY, 60, 60);
      } catch (err) {
        console.warn("Image not loaded:", animal.photo);
      }
    }

    const textX = x + padding + 70;
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text(`ID: ${animal.id || ""}`, textX, cursorY + 10);
    doc.text(`Source: ${animal.source || ""}`, textX, cursorY + 25);
    doc.text(`Listing ID: ${animal.listingId || ""}`, textX, cursorY + 40);

    if (animal.link) {
      doc.setTextColor(0, 0, 200);
      doc.setFont(undefined, "normal");
      doc.textWithLink("Open Listing", textX, cursorY + 55, { url: animal.link });
      doc.setTextColor(0, 0, 0);
    }

    // --- Title with dynamic font size (no background) ---
    cursorY += 75;
    let titleFontSize = 11;
    doc.setFont(undefined, "bold");

    let titleLines = doc.splitTextToSize(animal.title || "", cardWidth - 2 * padding - 6);
    const maxTitleHeight = 40; // maximum allowed height
    const lineHeight = 12;

    while (titleLines.length * lineHeight > maxTitleHeight && titleFontSize > 7) {
      titleFontSize -= 0.5;
      doc.setFontSize(titleFontSize);
      titleLines = doc.splitTextToSize(animal.title || "", cardWidth - 2 * padding - 6);
    }

    // Draw title text
    doc.setTextColor(0, 50, 100);
    doc.text(titleLines, x + padding + 3, cursorY);
    doc.setTextColor(0, 0, 0);

    // Update cursorY below title
    cursorY += titleLines.length * lineHeight + 4;

    // --- Description with dynamic font size ---
    if (animal.description) {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = animal.description;

      const lines = [];
      Array.from(tempDiv.childNodes).forEach((node) => {
        if (node.nodeName === "P") {
          lines.push(node.textContent);
          lines.push("");
        } else if (node.nodeName === "BR") {
          lines.push("");
        } else if (node.nodeType === Node.TEXT_NODE) {
          lines.push(node.textContent);
        }
      });

      let descFontSize = 9;
      let descLines = doc.splitTextToSize(lines.join("\n"), cardWidth - 2 * padding);
      const maxDescHeight = y + cardHeight - 60 - cursorY; // leave space for bottom info

      while (descLines.length * descFontSize * 1.2 > maxDescHeight && descFontSize > 6) {
        descFontSize -= 0.5;
        doc.setFontSize(descFontSize);
        descLines = doc.splitTextToSize(lines.join("\n"), cardWidth - 2 * padding);
      }

      doc.setFontSize(descFontSize);
      doc.setFont(undefined, "normal");
      doc.text(descLines, x + padding, cursorY);
    }

    // --- Bottom info ---
    const bottomY = y + cardHeight - 40;
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.text(`Price: ${animal.finalPrice || "-"}`, x + padding, bottomY);
    doc.setFontSize(7); // smaller font for long Close Date
    doc.text(`Close Date: ${animal.closeDate || "-"}`, x + cardWidth / 2, bottomY);
    doc.text(`Qty: ${animal.qty || "-"}`, x + cardWidth / 2, bottomY + 15);
    doc.setFontSize(9);
    doc.text(`Status: ${animal.status || "-"}`, x + padding, bottomY + 15);

    // --- Move to next card ---
    if ((index + 1) % 2 === 0) {
      x = margin;
      y += cardHeight + cardSpacing;
    } else {
      x += cardWidth + cardSpacing;
    }

    // New page every 4 cards
    if ((index + 1) % 4 === 0 && index !== animals.length - 1) {
      doc.addPage();
      drawHeader(doc, logoImg, pageWidth, margin, pageTitle);
      x = margin;
      y = margin + 20;
    }
  });

  // --- Summary Page ---
  if (summaryData && summaryData.length > 0) {
    doc.addPage();
    drawHeader(doc, logoImg, pageWidth, margin, pageTitle);

    // Offset summary below header/logo
    const summaryStartY = margin + 60; // leave enough space below header
    let summaryY = summaryStartY;

    // Summary Title
    const summaryTitleHeight = 28;
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(5, 125, 222);
    doc.rect(margin, summaryY, pageWidth - 2 * margin, summaryTitleHeight, "F");
    doc.text("Summary", margin + 10, summaryY + 20);

    summaryY += summaryTitleHeight + 15; // move below title

    summaryData.forEach((item) => {
      if (item.type === "row") {
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.setTextColor(0, 0, 0);

        // Label
        doc.text(item.label, margin + 10, summaryY);

        // Value with right alignment
        let valueText =
          item.dataType === "decimal"
            ? `$${item.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : item.value;
        doc.text(String(valueText), pageWidth - margin - 10, summaryY, { align: "right" });

        summaryY += 20;
      }

      if (item.type === "table") {
        const colWidths = [0.35, 0.2, 0.2, 0.25].map((p) => (pageWidth - 2 * margin) * p);
        let tableX = margin;
        let tableY = summaryY;

        // --- Draw header row ---
        doc.setFont(undefined, "bold");
        doc.setTextColor(0, 0, 0);
        let headerHeight = 18;

        colWidths.forEach((w, i) => {
          doc.setFillColor(245, 245, 245);
          doc.setDrawColor(200);
          doc.rect(tableX, tableY, w, headerHeight, "FD"); // F=fill, D=draw border

          // Align header text
          let align = "left";
          if (i === 1) align = "center";
          else if (i === 2 || i === 3) align = "right";

          let textX;
          if (align === "left") textX = tableX + 5;
          else if (align === "center") textX = tableX + w / 2;
          else textX = tableX + w - 5;

          doc.text(item.headers[i], textX, tableY + 13, { align });

          tableX += w;
        });

        tableY += headerHeight;
        tableX = margin;
        doc.setFont(undefined, "normal");

        // --- Draw table rows ---
        const rowHeight = 16;
        item.data.forEach((row, rowIndex) => {
          // Alternate row background
          if (rowIndex % 2 === 0) {
            doc.setFillColor(250, 250, 250);
            doc.rect(tableX, tableY, colWidths.reduce((a, b) => a + b, 0), rowHeight, "F");
          }

          // Cells
          let cellX = margin;
          row.forEach((cell, i) => {
            const colType = item.columnTypes?.[i] || (typeof cell === "number" ? "number" : "string");
            let cellValue;

            if (colType === "decimal") {
              cellValue = `$${Number(cell).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            } else {
              cellValue = cell;
            }

            // Alignment
            let align = "left";
            if (i === 1) align = "center";
            else if (i === 2 || i === 3) align = "right";

            let textX;
            if (align === "left") textX = cellX + 5;
            else if (align === "center") textX = cellX + colWidths[i] / 2;
            else textX = cellX + colWidths[i] - 5;

            doc.text(String(cellValue), textX, tableY + 12, { align });

            // Draw cell border
            doc.setDrawColor(200);
            doc.rect(cellX, tableY, colWidths[i], rowHeight);

            cellX += colWidths[i];
          });

          tableY += rowHeight;
        });

        summaryY = tableY + 20; // space after table
      }
    });
  }

  // --- Save PDF with timestamp ---
  const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
  doc.save(`Price_Comparison_Data_${timestamp}.pdf`);
};

// --- Header/logo ---
function drawHeader(doc, logoImg, pageWidth, margin, pageTitle) {
  try {
    doc.addImage(logoImg, "PNG", margin, 15, 70, 35);
  } catch (err) {
    console.warn("Logo not loaded:", err);
  }
  doc.setFontSize(14);
  doc.setFont(undefined, "bold");
  const title = pageTitle;
  const titleWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - titleWidth) / 2, 45);
}

// --- Load image helper ---
function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => {
      console.warn("Failed to load image:", url);
      resolve("");
    };
    img.src = url;
  });
}
